import React, { useEffect, useState, useMemo } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import Button from "../../../../component-lib/Molecules/button/Button";
import { TEXT_CONSTANTS } from "../../../../utils/text-constants/textConstants";
import SelectDropdown from "../../../../component-lib/Atoms/Select/Select";
import { VERIFY_ORDER_STATUS } from "./verifyConstant";
import { styled } from "@mui/material/styles";
import PatientListService from "../../../../services/PatientListService";
import { fetchAllPages } from "../../../../utils/fetchAllPages";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: "16px 24px",
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
  "& .MuiPaper-root": {
    BorderAllRounded: "8px",
    Height: "436px",
    width: "708px", // Set your custom width here
    maxWidth: "none", // Prevents Material-UI from overriding your width
  },
}));

interface OrderStatus {
  id: string;
  status: string;
  createdAt: string;
}

interface DefaultSelectedData {
  data?: {
    orderStatus?: string;
  };
}

interface VerifyOrderStatusDialogProps {
  isOpen: boolean;
  label: string;
  defaultSelectedData: DefaultSelectedData;
  handleData: (data: Record<string, unknown>) => void;
  handleClose: () => void;
}

export default function VerifyOrderStatusDialog({
  isOpen = false,
  label = "Your label here",
  defaultSelectedData,
  handleClose,
  handleData,
}: VerifyOrderStatusDialogProps) {
  const [orderStatusOptions, setOrderStatusOptions] = useState<Array<{ label: string; value: string }>>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchOrderStatuses = async () => {
      if (isOpen) {
        setLoading(true);
        try {
          // Fetch ALL order statuses (page through everything) so the dropdown
          // is never truncated by the page limit.
          const statuses = await fetchAllPages(
            PatientListService.getOrderStatus,
            (res) => res?.data?.orderStatus,
          );
          if (statuses?.length) {
            const mappedOptions = statuses.map((status: OrderStatus) => ({
              label: status.status,
              value: status.status,
            }));
            setOrderStatusOptions(mappedOptions);
          }
        } catch (error) {
          console.log("Error fetching order statuses:", error);
          setOrderStatusOptions([]);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchOrderStatuses();
  }, [isOpen]);

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        [VERIFY_ORDER_STATUS.STATUS]: Yup.string()
          .required("Status is required")
          .oneOf(
            orderStatusOptions.map((option) => option.value),
            "Invalid status"
          ),
      }),
    [orderStatusOptions]
  );
  return (
    <BootstrapDialog
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          handleClose();
        }
      }}
      aria-labelledby="customized-dialog-title"
      open={isOpen}
    >
      <Formik
        initialValues={{
          [VERIFY_ORDER_STATUS?.STATUS]: `${defaultSelectedData?.data?.orderStatus || ""}`,
        }}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={(values, { setSubmitting }) => {
          try {
            // getFormData(values);
            handleData(values);
          } catch (error) {
            console.log(error);
          } finally {
            setSubmitting(false); // Reset submitting state
          }
        }}
      >
        {({
          errors,
          values,
          touched,
          setFieldValue,
          handleBlur,
          handleSubmit,
        }) => {
          return (
            <Form onSubmit={handleSubmit}>
              <DialogTitle
                sx={{
                  m: 0,
                  p: 2,
                  fontWeight: 600,
                  fontSize: "18px",
                  fontFamily: "inter",
                }}
                id="customized-dialog-title"
              >
                {label}
              </DialogTitle>
              <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={(theme) => ({
                  position: "absolute",
                  right: 8,
                  top: 8,
                  color: theme.palette.grey[500],
                })}
              >
                <CloseIcon />
              </IconButton>
              <hr />
              {/* MAIN CONTENT */}
              <DialogContent>
                <div className="mt-5">
                  <SelectDropdown
                    label="Order Status"
                    value={values[VERIFY_ORDER_STATUS?.STATUS]}
                    options={orderStatusOptions}
                    onChangeCb={(e) =>
                      setFieldValue(VERIFY_ORDER_STATUS?.STATUS, e.target.value)
                    }
                    onBlur={handleBlur}
                    error={
                      touched[VERIFY_ORDER_STATUS?.STATUS] &&
                      Boolean(errors[VERIFY_ORDER_STATUS?.STATUS])
                    }
                    helperText={
                      touched[VERIFY_ORDER_STATUS?.STATUS]
                        ? errors[VERIFY_ORDER_STATUS?.STATUS]
                        : undefined
                    }
                    disabled={loading}
                  />
                </div>
              </DialogContent>

              {/* ACTION BUTTON */}
              <DialogActions style={{ padding: "24px" }}>
                <Button
                  label={TEXT_CONSTANTS?.CANCEL}
                  variant="outlined"
                  onClickCb={() => {
                    handleClose();
                  }}
                  style={{
                    width: "120px",
                    height: "44px",
                    padding: "12px 20px",
                    fontSize: "16px",
                    backgroundColor: "white",
                    border: "none",
                    color: "#393939",
                  }}
                />
                <Button
                  type="submit"
                  label={TEXT_CONSTANTS?.SAVE}
                  variant="contained"
                  onClickCb={() => {}}
                  style={{
                    padding: "8px 55px",
                    fontSize: "16px",
                  }}
                />
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </BootstrapDialog>
  );
}
